export const VIEWING_TAG_SELECTED_SESSION_KEY = 'viewingInvestor_selected';
export const VIEWING_TAG_DEFAULT_APPLIED_SESSION_KEY =
  'viewingTag_defaultApplied';
export const VIEWING_TAG_SESSION_USER_KEY = 'viewingTag_sessionUserId';

const IMPERSONATION_FRAME_PARAM = 'impersonationFrame';
const FRAME_SCOPE_PREFIX = `${IMPERSONATION_FRAME_PARAM}:`;

/**
 * Set once a same-origin impersonation frame is detected. The frame and its
 * parent share `sessionStorage`, and client-side navigation can drop the
 * frame query. The marker is copied onto `window.name`, which survives a
 * reload of this browsing context and is not shared with the parent.
 * `undefined` means "not yet detected". A parent document stays `undefined`
 * and re-checks, so it never claims a frame scope.
 */
let impersonationFrameScope: string | undefined;

function readSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Session storage may be unavailable in privacy-restricted browsers.
  }
}

function removeSessionItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Session storage may be unavailable in privacy-restricted browsers.
  }
}

function frameScope(marker: string): string {
  return `${FRAME_SCOPE_PREFIX}${marker}`;
}

function readQueryFrameMarker(): string | undefined {
  try {
    const marker = new URLSearchParams(window.location.search)
      .get(IMPERSONATION_FRAME_PARAM)
      ?.trim();
    return marker ? marker : undefined;
  } catch {
    return undefined;
  }
}

/** Marker copied onto this frame so a reload can recover it after navigation. */
function readPersistedFrameMarker(): string | undefined {
  try {
    const name = window.name;
    if (!name.startsWith(FRAME_SCOPE_PREFIX)) {
      return undefined;
    }
    const marker = name.slice(FRAME_SCOPE_PREFIX.length).trim();
    return marker ? marker : undefined;
  } catch {
    return undefined;
  }
}

function persistFrameMarker(marker: string): void {
  const name = frameScope(marker);
  try {
    if (window.name !== name) {
      window.name = name;
    }
  } catch {
    // window.name may be unwritable in a locked browsing context.
  }
}

function detectImpersonationFrameScope(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    if (window.parent === window) {
      return undefined;
    }
  } catch {
    return undefined;
  }

  const queryMarker = readQueryFrameMarker();
  const marker = queryMarker ?? readPersistedFrameMarker();
  if (!marker) {
    return undefined;
  }
  persistFrameMarker(marker);
  return frameScope(marker);
}

function currentImpersonationFrameScope(): string | undefined {
  if (impersonationFrameScope !== undefined) {
    return impersonationFrameScope;
  }
  const detected = detectImpersonationFrameScope();
  if (detected !== undefined) {
    impersonationFrameScope = detected;
  }
  return detected;
}

/**
 * Drop the in-memory frame latch so the next read detects this document again.
 * The browsing-context marker on `window.name` is left in place; a reload keeps it.
 */
export function resetViewingTagSessionScope(): void {
  impersonationFrameScope = undefined;
}

/** Storage key for this document. Impersonation frames do not use the parent's key. */
export function viewingTagSessionStorageKey(baseKey: string): string {
  const scope = currentImpersonationFrameScope();
  if (scope === undefined) {
    return baseKey;
  }
  return `${baseKey}::${scope}`;
}

/** User id that last wrote the viewing-tag session keys. */
export function readViewingTagSessionUserId(): number | null {
  const raw = readSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_SESSION_USER_KEY),
  );
  if (raw === null || raw.trim() === '') {
    return null;
  }
  const userId = Number(raw);
  return Number.isFinite(userId) ? userId : null;
}

export function writeViewingTagSessionUserId(userId: number): void {
  writeSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_SESSION_USER_KEY),
    String(userId),
  );
}

export function hasAppliedViewingTagDefaultThisSession(): boolean {
  return (
    readSessionItem(
      viewingTagSessionStorageKey(VIEWING_TAG_DEFAULT_APPLIED_SESSION_KEY),
    ) === '1'
  );
}

export function markViewingTagDefaultAppliedThisSession(): void {
  writeSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_DEFAULT_APPLIED_SESSION_KEY),
    '1',
  );
}

export function readViewingTagSelectedSession(): string | null {
  return readSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_SELECTED_SESSION_KEY),
  );
}

export function writeViewingTagSelectedSession(value: string | null): void {
  const key = viewingTagSessionStorageKey(VIEWING_TAG_SELECTED_SESSION_KEY);
  if (value === null || value === '') {
    removeSessionItem(key);
    return;
  }
  writeSessionItem(key, value);
}

/** Drops the selection, the default-applied marker, and the session owner. */
export function clearViewingTagSession(): void {
  removeSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_SELECTED_SESSION_KEY),
  );
  removeSessionItem(
    viewingTagSessionStorageKey(VIEWING_TAG_DEFAULT_APPLIED_SESSION_KEY),
  );
  removeSessionItem(viewingTagSessionStorageKey(VIEWING_TAG_SESSION_USER_KEY));
}

// Capture the marker before client-side navigation can drop the frame query.
currentImpersonationFrameScope();
